import React, { useEffect, useState } from "react";
import { getDocs, limit, query } from "firebase/firestore";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { customerSearchQuery, customersQuery } from "../lib/queries";
import { primaryToken, searchWords, matchesAllWords } from "../lib/search";

const OPTIONS = 10;

// Customer records matching what's typed (or the newest ones), fetched from
// Firestore. owner: a distributorId, or null for LiFE direct customers.
export function useCustomerOptions(owner, input, enabled = true) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || owner === undefined) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const words = searchWords(input);
        const token = primaryToken(words);
        const q = token
          ? query(customerSearchQuery(owner, token), limit(OPTIONS * 3))
          : query(customersQuery(owner), limit(OPTIONS));
        const snapshot = await getDocs(q);
        if (cancelled) return;
        const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOptions(
          rows
            .filter((c) => !token || matchesAllWords(c, words))
            .slice(0, OPTIONS),
        );
      } catch (error) {
        console.error("Customer search failed", error);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [owner, input, enabled]);

  return { options, loading };
}

export const renderCustomerOption = ({ key, ...props }, option) => (
  <li key={option.id} {...props}>
    <Box>
      <Typography variant="body2">{option.name}</Typography>
      <Typography variant="caption" color="text.secondary">
        {[option.location, option.email].filter(Boolean).join(" · ")}
      </Typography>
    </Box>
  </li>
);

// Pick an existing customer record (no free text).
export default function CustomerPicker({
  owner,
  value,
  onChange,
  label = "Customer record",
  helperText,
}) {
  const [input, setInput] = useState("");
  const { options, loading } = useCustomerOptions(owner, input);
  const all =
    value && !options.some((o) => o.id === value.id)
      ? [value, ...options]
      : options;

  return (
    <Autocomplete
      options={all}
      value={value}
      loading={loading}
      onChange={(_, next) => onChange(next)}
      onInputChange={(_, next) => setInput(next)}
      filterOptions={(x) => x}
      getOptionLabel={(option) => option.name || ""}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      renderOption={renderCustomerOption}
      noOptionsText={input ? "No matching customers" : "No customers yet"}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Type to search"
          helperText={helperText}
        />
      )}
    />
  );
}
